import * as React from "react";
import { entries, fill, flatten, groupBy, takeRight, zipWith } from "lodash";
import { Moment } from "moment";
import {
  Area,
  AreaChart,
  AxisDomain,
  Bar,
  BarChart,
  Legend,
  PositionType,
  ResponsiveContainer,
  ScaleType,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Desktop, Mobile, Tablet } from "containers/Responsive/Div3";
import { formatBigNumber, formatMoney } from "lib/helpers";
import { EarningByRegion } from "services/valuation/types";
import theme from "theme";

import { CustomTooltip } from "./CustomTooltip";

interface ChartEntry {
  date: Moment;
  amount: number | EarningByRegion;
}

interface Props {
  data: ChartEntry[];
  distributionFrequency: number;
}

type ChartType = "area-chart" | "bar-chart" | "stacked-bar-chart";

interface PrivateProps {
  data: ChartEntry[];
  chartType?: ChartType;
  padding: number;
  height: number;
  marginVertical: number;
  marginHorizontal: number;
  isMobile: boolean;
  strokeColor?: string;
  fillColor?: string;
  shouldHideXLabel?: boolean;
  secondXAxisCustomTicks?: React.ReactNode;
  xLabelPosition?: PositionType;
  customYAxisDomain?: [AxisDomain, AxisDomain];
  customYAxisScale?: ScaleType;
  yLabelOffset?: number;
  distributionFrequency: number;
  showUnreportedRegion?: boolean;
}

export const Chart: React.FC<PrivateProps> = React.memo(
  ({
    data,
    chartType,
    padding,
    height,
    marginVertical,
    marginHorizontal,
    isMobile,
    strokeColor,
    fillColor,
    shouldHideXLabel,
    secondXAxisCustomTicks,
    customYAxisDomain,
    customYAxisScale,
    yLabelOffset,
    xLabelPosition,
    distributionFrequency,
    showUnreportedRegion,
  }) => {
    const getPeriodLabel = (date: Moment) => {
      if (distributionFrequency === 1) {
        return "1Y";
      } else if (distributionFrequency === 2) {
        return date.quarter() < 3 ? "1H" : "2H";
      } else if (distributionFrequency === 4) {
        return `${date.quarter()}Q`;
      } else {
        return date.format("MMM");
      }
    };

    const parsedData = flatten(
      entries(groupBy(data, (x: ChartEntry) => x.date.year()))
        .map(([year, entriesArray]) => ({
          // if monthly distribution and we have a single bar left on the given
          // year we don't display the year to avoid overflowing text
          yearAxis:
            distributionFrequency === 12 &&
            entriesArray.length < 2 &&
            year === String(Math.min(...data.map(x => x.date.year())))
              ? fill(Array(entriesArray.length), "")
              : [String(year), ...fill(Array(entriesArray.length - 1), "")],
          entries: entriesArray,
        }))
        .map(({ entries, yearAxis }) =>
          zipWith(entries, yearAxis, (entry, yearLabel) => {
            const dataItem = {
              x: `${getPeriodLabel(entry.date)} ${entry.date.year()}`,
              x1: getPeriodLabel(entry.date),
              x2: yearLabel,
            };

            if (typeof entry.amount === "number") {
              return {
                ...dataItem,
                y: parseFloat(formatMoney(entry.amount, 0, ".", "")),
              };
            } else {
              return {
                ...dataItem,
                domestic: parseFloat(
                  formatMoney(entry.amount.domestic, 0, ".", ""),
                ),
                international: parseFloat(
                  formatMoney(entry.amount.intl, 0, ".", ""),
                ),
                unreported: parseFloat(
                  formatMoney(entry.amount.unreported, 0, ".", ""),
                ),
                y: parseFloat(formatMoney(entry.amount.total, 0, ".", "")),
              };
            }
          }),
        ),
    );

    const CustomLegend = (value: any) => (
      <span
        style={{
          color: "black",
          textTransform: "capitalize",
          fontSize: "0.8em",
        }}
      >
        {value}
      </span>
    );

    const chartWrapperMap = new Map<ChartType, any>([
      ["area-chart", AreaChart],
      ["bar-chart", BarChart],
      ["stacked-bar-chart", BarChart],
    ]);

    const makeChartMap = (showUnreportedRegion: boolean) => {
      const chartMap = new Map<ChartType, React.ReactNode[]>([
        [
          "area-chart",
          [
            <Area
              key="area-chart"
              type="linear"
              dataKey="y"
              fill={fillColor || "#EB57571A"}
              stroke={strokeColor || "#EB5757"}
              strokeWidth={3}
              isAnimationActive={false}
            />,
          ],
        ],
        [
          "bar-chart",
          [
            <Bar
              key="bar-chart"
              layout="horizontal"
              dataKey="y"
              fill={fillColor || "#EB5757"}
              isAnimationActive={false}
            />,
          ],
        ],
        [
          "stacked-bar-chart",
          [
            <Tooltip key="stacked-bar-tooltip" content={<CustomTooltip />} />,
            <Bar
              key="domestic-bar-chart"
              layout="horizontal"
              dataKey="domestic"
              fill={theme.custom.palette.purple.main}
              stackId="earnings"
              isAnimationActive={false}
            />,
            <Bar
              key="international-bar-chart"
              layout="horizontal"
              dataKey="international"
              fill="#5BCD9D"
              stackId="earnings"
              isAnimationActive={false}
            />,
            <Legend
              key="stacked-bar-legend"
              formatter={CustomLegend}
              wrapperStyle={{ marginLeft: 45 }}
            />,
          ],
        ],
      ]);
      if (showUnreportedRegion) {
        chartMap
          .get("stacked-bar-chart")
          .splice(
            3,
            0,
            <Bar
              key="unreported-bar-chart"
              layout="horizontal"
              dataKey="unreported"
              fill="#ccc"
              stackId="earnings"
              isAnimationActive={false}
            />,
          );
      }
      return chartMap;
    };

    const chartMap = makeChartMap(showUnreportedRegion);

    const ChartWrapper =
      chartWrapperMap.get(chartType) || chartWrapperMap.get("area-chart");
    const chartElement = chartMap.get(chartType) || chartMap.get("area-chart");

    return (
      <>
        <ResponsiveContainer
          width="99%"
          height={height}
          className="chart-container"
        >
          <ChartWrapper
            data={parsedData}
            margin={{
              top: marginVertical,
              right: marginHorizontal,
              left: marginHorizontal,
              bottom: marginVertical,
            }}
          >
            <XAxis
              dataKey="x"
              stroke="#2A313B"
              xAxisId={0}
              tickLine={false}
              padding={{ left: padding, right: padding }}
              tickFormatter={value => (value as string).split(" ")[0]}
            />
            <XAxis
              dataKey="x2"
              xAxisId={1}
              stroke="1"
              padding={{ left: padding, right: padding }}
              label={
                !(shouldHideXLabel || false) && {
                  value: "Earning Period",
                  position: xLabelPosition || "bottom",
                }
              }
              tick={
                typeof secondXAxisCustomTicks === "object"
                  ? secondXAxisCustomTicks
                  : null
              }
              interval={0}
            />
            <YAxis
              label={{
                value: "USD",
                angle: -90,
                position: "insideLeft",
                offset: isMobile ? 16 : yLabelOffset || -10,
              }}
              tickLine={false}
              stroke="#2A313B"
              tickFormatter={isMobile ? formatBigNumber : undefined}
              domain={customYAxisDomain || [0, "auto"]}
              scale={customYAxisScale || "auto"}
            />
            {chartType !== "stacked-bar-chart" && (
              <Tooltip
                formatter={value => [
                  `$${formatMoney(value as number, 0)}`,
                  "amount",
                ]}
              />
            )}
            {chartElement}
          </ChartWrapper>
        </ResponsiveContainer>
      </>
    );
  },
);

export const RevenueChart: React.FC<Props> = React.memo(
  ({ data, distributionFrequency }) => (
    <>
      <Desktop>
        <Chart
          data={takeRight(data, 12)}
          padding={0}
          height={400}
          marginHorizontal={20}
          marginVertical={20}
          isMobile={false}
          distributionFrequency={distributionFrequency}
        />
      </Desktop>
      <Tablet>
        <Chart
          data={takeRight(data, 12)}
          padding={0}
          height={300}
          marginHorizontal={20}
          marginVertical={20}
          isMobile={false}
          distributionFrequency={distributionFrequency}
        />
      </Tablet>
      <Mobile>
        <Chart
          data={takeRight(data, 6)}
          padding={0}
          height={220}
          marginHorizontal={0}
          marginVertical={20}
          isMobile
          distributionFrequency={distributionFrequency}
        />
      </Mobile>
    </>
  ),
);
